import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import couponService from "../../services/couponService";

// Initial state
const initialState = {
  coupons: [],
  coupon: null,
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  totalPages: 0,
  currentPage: 1,
  validatedCoupon: null,
  validationStatus: "idle",
  validationError: null,
};

// Async thunks
export const fetchCoupons = createAsyncThunk(
  "coupons/fetchAll",
  async (params, { rejectWithValue }) => {
    try {
      return await couponService.getAllCoupons(params);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch coupons"
      );
    }
  }
);

export const fetchCouponById = createAsyncThunk(
  "coupons/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      return await couponService.getCouponById(id);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch coupon"
      );
    }
  }
);

export const createCoupon = createAsyncThunk(
  "coupons/create",
  async (couponData, { rejectWithValue }) => {
    try {
      return await couponService.createCoupon(couponData);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create coupon"
      );
    }
  }
);

export const updateCoupon = createAsyncThunk(
  "coupons/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await couponService.updateCoupon(id, data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update coupon"
      );
    }
  }
);

export const deleteCoupon = createAsyncThunk(
  "coupons/delete",
  async (id, { rejectWithValue }) => {
    try {
      await couponService.deleteCoupon(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete coupon"
      );
    }
  }
);

export const validateCoupon = createAsyncThunk(
  "coupons/validate",
  async (data, { rejectWithValue }) => {
    try {
      return await couponService.validateCoupon(data);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Invalid coupon");
    }
  }
);

// Coupon slice
const couponSlice = createSlice({
  name: "coupons",
  initialState,
  reducers: {
    clearCouponError: (state) => {
      state.error = null;
    },
    clearCoupon: (state) => {
      state.coupon = null;
    },
    clearValidatedCoupon: (state) => {
      state.validatedCoupon = null;
      state.validationStatus = "idle";
      state.validationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all coupons
      .addCase(fetchCoupons.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.coupons = action.payload.coupons || [];
        state.totalPages = action.payload.totalPages || 1;
        state.currentPage = action.payload.currentPage || 1;
      })
      .addCase(fetchCoupons.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Fetch one coupon
      .addCase(fetchCouponById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCouponById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.coupon = action.payload;
      })
      .addCase(fetchCouponById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Create coupon
      .addCase(createCoupon.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createCoupon.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.coupons.unshift(action.payload);
      })
      .addCase(createCoupon.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update coupon
      .addCase(updateCoupon.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateCoupon.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.coupons.findIndex(
          (coupon) => coupon.id === action.payload.id
        );
        if (index !== -1) {
          state.coupons[index] = action.payload;
        }
        state.coupon = action.payload;
      })
      .addCase(updateCoupon.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Delete coupon
      .addCase(deleteCoupon.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteCoupon.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.coupons = state.coupons.filter(
          (coupon) => coupon.id !== action.payload
        );
      })
      .addCase(deleteCoupon.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Validate coupon
      .addCase(validateCoupon.pending, (state) => {
        state.validationStatus = "loading";
        state.validationError = null;
      })
      .addCase(validateCoupon.fulfilled, (state, action) => {
        state.validationStatus = "succeeded";
        state.validatedCoupon = action.payload;
      })
      .addCase(validateCoupon.rejected, (state, action) => {
        state.validationStatus = "failed";
        state.validationError = action.payload;
        state.validatedCoupon = null;
      });
  },
});

export const { clearCouponError, clearCoupon, clearValidatedCoupon } =
  couponSlice.actions;
export default couponSlice.reducer;
